import * as VideoConference from 'video-conference';
import * as VoxImplant from 'voximplant-websdk';
import {AudioSourceInfo, VideoSourceInfo} from "voximplant-websdk/Structures";
import VideoManager from "./VideoManager";

/**
 * Менеджер устройств
 */
export default class DevicesManager implements VideoConference.DevicesManagerInterface {

    private micId: string|null = null;
    private cameraId: string|null = null;
    private camerasList: Array<VideoConference.MediaDeviceInfo> = [];
    private microphonesList: Array<VideoConference.MediaDeviceInfo> = [];
    private localStream: MediaStream|null  = null;
    private videoManager: VideoManager|null = null;
    public user: VideoConference.UserData = {
        cameraStatus: true,
        microphoneStatus: true,
        audioStatus: true,
    };

    /**
     * @inheritDoc
     */
    public async initDevices(): Promise<void>
    {
        await DevicesManager.getCamerasData().then((camerasInfo: Array<VideoSourceInfo>) => {
            camerasInfo.forEach((info: VideoSourceInfo) => {
                this.camerasList.push({
                    id: info.id.toString(),
                    name: info.name,
                    group: info.group,
                });
            });

            if (typeof this.camerasList[0] !== 'undefined' && this.camerasList[0].id) {
                this.cameraId = this.camerasList[0].id;
            }
        });

        await DevicesManager.getMicrophonesData().then((micInfo: Array<AudioSourceInfo>) => {
            micInfo.forEach((info: AudioSourceInfo) => {
                this.microphonesList.push({
                    id: info.id.toString(),
                    name: info.name,
                    group: info.group,
                });
            });

            if (typeof this.microphonesList[0] !== 'undefined' && this.microphonesList[0].id) {
                this.micId = this.microphonesList[0].id;
            }
        });
    }

    /**
     * @inheritDoc
     */
    public getCameras(): Array<VideoConference.MediaDeviceInfo>
    {
        return this.camerasList;
    }

    /**
     * @inheritDoc
     */
    public getMicrophones(): Array<VideoConference.MediaDeviceInfo>
    {
        return this.microphonesList;
    }

    /**
     * @inheritDoc
     */
    public async changeCamera(id: string): Promise<void>
    {
        await this.changeLocalCamera(id)
            .then((e: MediaStream) => {
                let localVideoElement = document.getElementById('userVideoPreview');

                if (localVideoElement instanceof HTMLVideoElement) {
                    localVideoElement.srcObject = e;
                    localVideoElement.play().catch((reason: any) => {
                        console.error('Video play fail. By reason', reason);
                    });``
                }

            });
    }

    /**
     * @inheritDoc
     */
    public async changeMicrophone(id: string): Promise<void>
    {
        await this.changeLocalMic(id);
    }

    /**
     * @inheritDoc
     */
    public async setCallSettings(): Promise<void>
    {
        const { videoSettings, audioSettings } = this.generateConstraints();
        const call = this.videoManager?.getCurrentConf();

        if (!call) {
            return;
        }

        await VoxImplant.Hardware.CameraManager.get().setCallVideoSettings(call, videoSettings)
            .catch((reason) => {
                console.error('setCallVideoSettings fail: ', reason);
            });

        await VoxImplant.Hardware.CameraManager.get().setDefaultVideoSettings(videoSettings);

        await VoxImplant.Hardware.AudioDeviceManager.get().setCallAudioSettings(call, audioSettings)
            .catch((reason) => {
                console.error('setCallAudioSettings fail: ', reason);
            });

        await VoxImplant.Hardware.AudioDeviceManager.get().setDefaultAudioSettings(audioSettings);
    }


    /**
     * Данные камер от воксимпланта
     * @private
     */
    private static async getCamerasData(): Promise<Array<VideoSourceInfo>>
    {
        return await VoxImplant.Hardware.CameraManager.get().getInputDevices();
    }

    /**
     * Данные микрофонов от воксимпланта
     * @private
     */
    private static async getMicrophonesData(): Promise<Array<AudioSourceInfo>>
    {
        return await VoxImplant.Hardware.AudioDeviceManager.get().getInputDevices();
    }

    /**
     * Изменить камеру
     */
    private async changeLocalCamera(newCameraId: string): Promise<MediaStream>
    {
        this.cameraId = newCameraId;
        if (this.localStream) {
            this.localStream.getVideoTracks().forEach((tr: MediaStreamTrack) => tr.stop());
        }
        return await this.refreshCamera();
    }

    /**
     * Обновить камеру
     */
    public async refreshCamera(): Promise<MediaStream>
    {
        return await new Promise((resolve, reject) => {
            let constraints: any = {
                audio: false,
                video: {},
            };

            if (this.cameraId) {
                constraints.video.deviceId = { exact: this.cameraId };
            }

            navigator.mediaDevices
                .getUserMedia(constraints)
                .then((stream: MediaStream) => {
                    let audioTracks = this.localStream ? this.localStream.getAudioTracks() : [];

                    this.localStream = new MediaStream([
                        ...audioTracks,
                        ...stream.getVideoTracks(),
                    ]);
                    resolve(stream);
                })
                .catch(reject);
        });
    }

    /**
     * Изменить микрофон
     */
    private changeLocalMic(newMicrophoneId: string): Promise<MediaStream>
    {
        this.micId = newMicrophoneId;
        return new Promise((resolve, reject) => {
            let constraints: any = { audio: { deviceId: newMicrophoneId }, video: false };
            navigator.mediaDevices
                .getUserMedia(constraints)
                .then((stream) => {
                    let videoTracks = this.localStream ? this.localStream.getVideoTracks() : [];

                    this.localStream = new MediaStream([
                        ...stream.getAudioTracks(),
                        ...videoTracks,
                    ]);
                    resolve(stream);
                })
                .catch(reject);
        });
    }

    /**
     * Параметры подключения
     * @private
     */
    private generateConstraints(): { videoSettings: any; audioSettings: any; }
    {
        const videoSettings: any = {};

        if (this.cameraId) {
            videoSettings.cameraId = this.cameraId;
        }
        const audioSettings: any = {
            echoCancellation: true,
            autoGainControl: true,
            noiseSuppression: true,
            advanced: [
                { googEchoCancellation: { exact: true } },
                { googExperimentalEchoCancellation: { exact: true } },
                { autoGainControl: { exact: true } },
                { noiseSuppression: { exact: true } },
                { googHighpassFilter: { exact: true } },
                { googAudioMirroring: { exact: true } },
            ],
        };
        if (this.micId) {
            audioSettings.inputId = this.micId;
        }

        return { videoSettings, audioSettings };
    }

    /**
     * Состояние камеры
     */
    public getCameraStatus(): boolean
    {
        return this.user.cameraStatus;
    }

    /**
     * Установить состояние камеры
     */
    public setCameraStatus(status: boolean)
    {
        this.user.cameraStatus = status;
    }

    /**
     * Состояние микрофона
     */
    public getMicrophoneStatus(): boolean
    {
        return this.user.microphoneStatus;
    }

    /**
     * Установить состояние микрофона
     */
    public setMicrophoneStatus(status: boolean)
    {
        this.user.microphoneStatus = status;
    }

    /**
     * Состояние аудио
     */
    public getAudioStatus(): boolean
    {
        return this.user.audioStatus;
    }


    /**
     * Установить состояние аудио
     */
    public setAudioStatus(status: boolean)
    {
        this.user.audioStatus = status;
    }

    /**
     * id текущей камеры
     */
    public getCameraId(): string|null
    {
        return this.cameraId;
    }

    /**
     * id текущего микрофона
     */
    public getMicId(): string|null
    {
        return this.micId;
    }

    public async getLocalMedia(element: HTMLVideoElement)
    {
        let constraints: any = {
            audio: {},
            video: {},
        };

        if (this.micId) {
            constraints.audio.deviceId = this.micId;
        }

        if (this.cameraId) {
            constraints.video.deviceId = this.cameraId;
        }

        await navigator.mediaDevices
            .getUserMedia(constraints)
            .then((stream) => {
                this.localStream = stream;
                let permissions = { video: true, audio: true };
                DevicesManager.showLocalMedia(element, permissions, stream);
            })
            .catch(() => {
                constraints.video = false;
                navigator.mediaDevices
                    .getUserMedia(constraints)
                    .then((stream) => {
                        this.localStream = stream;
                        let permissions = { video: false, audio: true };
                        DevicesManager.showLocalMedia(element, permissions, stream);
                    });
            });
    }

    public stopLocalMedia(): void
    {
        if (this.localStream) {
            this.localStream.getTracks().forEach((tr) => tr.stop());
            this.localStream = null;
        }
    }

    private static showLocalMedia(element: HTMLVideoElement, permissions: {video: boolean, audio: boolean}, stream: MediaStream): void
    {
        if (permissions.video) {
            element.srcObject = new MediaStream(stream.getVideoTracks());
        }
    }

    public setVideoManager(videoManager: VideoManager): void
    {
        this.videoManager = videoManager;
    }
}
